import { useTranslation } from "react-i18next";
import "../../../i18n";
import { KeyValues, Notice, Pipeline } from "../../../design/components";
import {
  INBOUND_STAGES,
  IN_STAGE_LABEL,
  IN_STAGE_NOTE,
  OUTBOUND_STAGES,
  STAGE_NOTE,
  stageIndex,
  type InboundStage,
  type OutboundStage,
} from "../../../domain/stages";
import { dateTime, relative } from "../../../lib/format";
import type { DocumentDetail, DocumentEvent } from "../../../api/types";
import styles from "./DocumentInspector.module.css";

function isOutboundStage(stage: string): stage is OutboundStage {
  return (OUTBOUND_STAGES as readonly string[]).includes(stage);
}

function isInboundStage(stage: string): stage is InboundStage {
  return (INBOUND_STAGES as readonly string[]).includes(stage);
}

/** Latest event timestamp for each outbound stage, positioned for Pipeline. */
export function stageTimes(events: DocumentEvent[]): (string | null)[] {
  const latest = new Map<string, string>();
  for (const event of events) {
    const current = latest.get(event.stage);
    if (!current || event.createdAt > current) latest.set(event.stage, event.createdAt);
  }
  return OUTBOUND_STAGES.map((stage) => {
    const at = latest.get(stage);
    return at ? dateTime(at) : null;
  });
}

export function StagePanel({ doc, events }: { doc: DocumentDetail; events: DocumentEvent[] }) {
  const { t } = useTranslation();
  const outbound = doc.direction === "out";
  const index = isOutboundStage(doc.stage) ? stageIndex(doc.stage) : 0;
  const failed = doc.state === "error";

  function errorCopy(): string {
    if (doc.errorClass === "identity") {
      return t(
        "documents.inspector.stages.identityError",
        "The document was stopped before it was sent because the participant ID did not match the company's commercial registration number.",
      );
    }
    if (doc.errorClass === "transient") {
      return doc.nextAttemptAt
        ? t("documents.inspector.stages.retryScheduledAt", "A retry is scheduled: next attempt {{when}}.", {
            when: relative(doc.nextAttemptAt),
          })
        : t("documents.inspector.stages.retryScheduled", "A retry is scheduled.");
    }
    return t("documents.inspector.stages.terminalError", "This document will not be retried automatically.");
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="grow">{t("documents.inspector.stages.title", "Stages")}</h2>
      </div>
      <div className="card-body">
        {outbound ? (
          <>
            <Pipeline
              current={index}
              // Archive is the last outbound stage; a published document is still on its way.
              complete={doc.stage === "archived"}
              times={stageTimes(events)}
              {...(failed ? { failedAt: index } : {})}
            />
            {isOutboundStage(doc.stage) ? (
              <p className={styles.note}>{STAGE_NOTE[doc.stage]}</p>
            ) : null}
          </>
        ) : (
          <KeyValues
            pairs={[
              [t("documents.inspector.stages.stage", "Stage"), isInboundStage(doc.stage) ? IN_STAGE_LABEL[doc.stage] : doc.stage],
              [
                t("documents.inspector.stages.whatHappensHere", "What happens here"),
                isInboundStage(doc.stage) ? IN_STAGE_NOTE[doc.stage] : "—",
              ],
            ]}
          />
        )}

        {failed ? (
          <Notice kind="fail">
            <div className="mono">{doc.errorCode ?? "—"}</div>
            {doc.errorMessage ? <div>{doc.errorMessage}</div> : null}
            <div>{errorCopy()}</div>
            <div className={styles.sub}>
              {doc.errorClass ?? t("documents.inspector.stages.error", "error")}
              {" · "}
              {t("documents.inspector.stages.attempt", "attempt {{count}}", { count: doc.attempts })}
              {doc.nextAttemptAt
                ? ` · ${t("documents.inspector.stages.nextAttempt", "next attempt {{when}}", {
                    when: dateTime(doc.nextAttemptAt),
                  })}`
                : ""}
            </div>
          </Notice>
        ) : null}
        {doc.leasedBy && doc.leasedUntil ? (
          <p className={styles.note}>
            Being processed by {doc.leasedBy} until {dateTime(doc.leasedUntil)}
          </p>
        ) : null}
      </div>
    </section>
  );
}
